import React, { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Search } from "lucide-react";
import "./SearchableSelect.css";

// Drop-in replacement for <select>: same props, and it still renders the real <select>
// (so styling, `required`, refs, form data and tests work as before). When a long list
// is opened with the mouse (or Space / F4 / Alt+Arrow), a popup with a search box at the
// top replaces the browser's list; picking an option sets the select and fires the normal
// input/change events. Short lists, touch input, `multiple`/`size` and disabled selects
// keep the native dropdown.
//  - searchThreshold: more options than this get the search popup (default 10)
//  - searchPlaceholder: placeholder of the search box

const GAP = 4; // px between the select and the popup
const EDGE = 8; // px kept free at the viewport / dialog edges
const MAX_HEIGHT = 340;
const MIN_HEIGHT = 140;
const MIN_WIDTH = 240;
const MAX_WIDTH = 440;
const TYPE_AHEAD_MS = 1000; // Space right after typing is part of the browser's type-ahead
const BODY_EVENTS = ["mousedown", "mouseup", "click", "pointerdown", "pointerup", "touchstart", "touchend"];

const normalize = (s) => (s || "").toLocaleLowerCase().replace(/\s+/g, " ").trim();

// The select's options as plain objects, read from the DOM so any children work.
const readOptions = (el) =>
  Array.from(el.options).map((o, index) => {
    const group = o.parentElement && o.parentElement.tagName === "OPTGROUP" ? o.parentElement : null;
    const label = o.label || o.text;
    return {
      index,
      label,
      group: group ? group.label : null,
      disabled: o.disabled || Boolean(group && group.disabled),
      hidden: o.hidden,
      text: normalize(`${label} ${group ? group.label : ""}`),
    };
  });

// Every word typed must appear in the option, in any order ("gauri pot" finds "Gauri Navnath Potpode").
const filterOptions = (options, query) => {
  const words = normalize(query).split(" ").filter(Boolean);
  return options.filter((o) => !o.hidden && words.every((w) => o.text.includes(w)));
};

const firstEnabled = (list, preferred) => {
  const keep = list.find((o) => o.index === preferred && !o.disabled);
  if (keep) return keep.index;
  const first = list.find((o) => !o.disabled);
  return first ? first.index : -1;
};

// Inside a dialog the popup must live in the dialog's DOM, or focus traps (Radix) pull focus back out.
const portalHost = (el) => el.closest('[role="dialog"], [role="alertdialog"]') || el.ownerDocument.body;

// Nearest ancestor that makes `position: fixed` relative to itself instead of the viewport.
const fixedContainingBlock = (node) => {
  for (let n = node; n && n !== n.ownerDocument.body; n = n.parentElement) {
    const s = window.getComputedStyle(n);
    if (
      (s.transform && s.transform !== "none") ||
      (s.filter && s.filter !== "none") ||
      (s.perspective && s.perspective !== "none") ||
      /paint|layout|strict|content/.test(s.contain || "") ||
      /transform|filter|perspective/.test(s.willChange || "")
    ) {
      return n;
    }
  }
  return null;
};

const SearchableSelect = forwardRef(function SearchableSelect(
  {
    searchThreshold = 10,
    searchPlaceholder = "Type to search...",
    children,
    onMouseDown,
    onPointerDown,
    onKeyDown,
    onFocus,
    onBlur,
    ...selectProps
  },
  ref
) {
  const selectRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const openRef = useRef(false); // read by event handlers before React re-renders
  const blockRef = useRef(null); // element the popup's `position: fixed` is relative to, if not the viewport
  const widthRef = useRef(null); // popup width, measured once per open
  const silenceFocus = useRef(false);
  const pointerType = useRef("mouse");
  const lastTypeAhead = useRef(0);
  const scrollMode = useRef(null); // "center" on open, "nearest" after arrow keys
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1); // DOM index of the highlighted option
  const [font, setFont] = useState({});
  const listId = useId();

  const visible = useMemo(() => filterOptions(options, query), [options, query]);

  const setRefs = useCallback(
    (node) => {
      selectRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  const usesPopup = () => {
    const el = selectRef.current;
    return Boolean(el) && !el.disabled && !el.multiple && !(el.size > 1) && el.options.length > searchThreshold;
  };

  // Focus going back to the select from the popup is not a new focus for the caller.
  const focusSelect = useCallback(() => {
    const el = selectRef.current;
    if (!el || el.ownerDocument.activeElement === el) return;
    silenceFocus.current = true;
    el.focus({ preventScroll: true });
    silenceFocus.current = false;
  }, []);

  const openPopup = () => {
    const el = selectRef.current;
    const opts = readOptions(el);
    const style = window.getComputedStyle(el);
    openRef.current = true;
    widthRef.current = null;
    scrollMode.current = "center";
    setOptions(opts);
    setSelectedIndex(el.selectedIndex);
    setQuery("");
    setActive(firstEnabled(filterOptions(opts, ""), el.selectedIndex));
    setFont({ fontFamily: style.fontFamily, fontSize: style.fontSize });
    setOpen(true);
  };

  const closePopup = useCallback(
    (refocus) => {
      openRef.current = false;
      setOpen(false);
      if (refocus) focusSelect();
    },
    [focusSelect]
  );

  const choose = (option) => {
    if (!option || option.disabled) return;
    const el = selectRef.current;
    closePopup(true);
    if (!el || el.selectedIndex === option.index) return;
    el.selectedIndex = option.index;
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  };

  // ---- the select itself ----

  const handlePointerDown = (e) => {
    pointerType.current = e.pointerType || "mouse";
    if (onPointerDown) onPointerDown(e);
  };

  const handleMouseDown = (e) => {
    if (onMouseDown) onMouseDown(e);
    if (e.defaultPrevented || e.button !== 0) return;
    if (pointerType.current === "touch" || pointerType.current === "pen") return; // native picker on touch
    if (!openRef.current && !usesPopup()) return;
    e.preventDefault(); // stops the browser's own list from opening
    if (openRef.current) {
      closePopup(true);
      return;
    }
    const el = selectRef.current;
    if (el.ownerDocument.activeElement !== el) el.focus({ preventScroll: true }); // a real focus, as a click gives
    openPopup();
  };

  const handleKeyDown = (e) => {
    if (onKeyDown) onKeyDown(e);
    const key = e.key || "";
    if (e.defaultPrevented || openRef.current) return;
    if (key.length === 1 && key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) {
      lastTypeAhead.current = Date.now();
      return;
    }
    const opensList =
      key === "F4" ||
      (e.altKey && (key === "ArrowDown" || key === "ArrowUp")) ||
      (key === " " && !e.ctrlKey && !e.metaKey && Date.now() - lastTypeAhead.current > TYPE_AHEAD_MS);
    if (opensList && usesPopup()) {
      e.preventDefault();
      openPopup();
    }
  };

  const handleFocus = (e) => {
    if (silenceFocus.current) return;
    if (onFocus) onFocus(e);
  };

  // While the popup is open the select is still "in use", as with the native list.
  const handleBlur = (e) => {
    if (openRef.current) return;
    if (onBlur) onBlur(e);
  };

  // ---- popup ----

  // Mount point: a div inside the dialog (or <body>) for as long as the popup is open.
  useLayoutEffect(() => {
    if (!open || !selectRef.current) return undefined;
    const host = portalHost(selectRef.current);
    const div = host.ownerDocument.createElement("div");
    div.className = "ss-portal";
    host.appendChild(div);
    // Popup clicks must not reach "click outside" handlers of whatever holds the select
    // (the browser's own list never sends them). Inside a dialog they already count as inside.
    const stop = (e) => e.stopPropagation();
    const isBody = host === host.ownerDocument.body;
    if (isBody) BODY_EVENTS.forEach((type) => div.addEventListener(type, stop));
    blockRef.current = fixedContainingBlock(div);
    setContainer(div);
    return () => {
      if (isBody) BODY_EVENTS.forEach((type) => div.removeEventListener(type, stop));
      div.remove();
      setContainer(null);
    };
  }, [open]);

  const place = useCallback(() => {
    const el = selectRef.current;
    const panel = panelRef.current;
    if (!el || !panel) return;
    const r = el.getBoundingClientRect();
    const doc = el.ownerDocument;
    let top = EDGE;
    let bottom = window.innerHeight - EDGE;
    let left = EDGE;
    let right = doc.documentElement.clientWidth - EDGE;
    // A transformed ancestor (e.g. a centred dialog) clips the popup to its box.
    const block = blockRef.current;
    if (block) {
      const b = block.getBoundingClientRect();
      top = Math.max(top, b.top + EDGE);
      bottom = Math.min(bottom, b.bottom - EDGE);
      left = Math.max(left, b.left + EDGE);
      right = Math.min(right, b.right - EDGE);
    }
    if ((r.width || r.height) && (r.bottom < top || r.top > bottom)) {
      closePopup(false); // the select scrolled out of view
      return;
    }
    const below = bottom - r.bottom - GAP;
    const above = r.top - GAP - top;
    const placeBelow = below >= Math.min(MAX_HEIGHT, 220) || below >= above;
    const maxHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, placeBelow ? below : above));
    panel.style.maxHeight = `${maxHeight}px`;
    // Like the browser's list: at least as wide as the select, wider for long options (measured
    // once per open, so the popup doesn't jump while typing).
    const room = Math.max(0, right - left);
    if (widthRef.current == null) {
      panel.style.minWidth = `${Math.max(r.width, MIN_WIDTH)}px`;
      panel.style.maxWidth = `${Math.max(r.width, MAX_WIDTH)}px`;
      panel.style.width = "max-content";
      const list = listRef.current;
      const scrollbar = list ? list.offsetWidth - list.clientWidth : 0;
      widthRef.current = Math.min(Math.ceil(panel.getBoundingClientRect().width) + scrollbar, Math.max(r.width, MAX_WIDTH));
      panel.style.minWidth = "";
      panel.style.maxWidth = "";
    }
    const width = Math.min(widthRef.current, room);
    panel.style.width = `${width}px`;
    const height = panel.offsetHeight;
    const wantTop = placeBelow ? r.bottom + GAP : r.top - GAP - height;
    const wantLeft = Math.min(Math.max(r.left, left), right - width);
    // Measure where top/left 0 lands, so this also works when the containing block isn't the viewport.
    panel.style.top = "0px";
    panel.style.left = "0px";
    const origin = panel.getBoundingClientRect();
    panel.style.top = `${wantTop - origin.top}px`;
    panel.style.left = `${wantLeft - origin.left}px`;
  }, [closePopup]);

  // Position, then focus the search box once the popup is in the DOM.
  useLayoutEffect(() => {
    if (!open || !container) return;
    place();
    if (inputRef.current && inputRef.current.ownerDocument.activeElement !== inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [open, container, place, visible.length]);

  // Keep the highlighted option in view (centred on open, nearest edge after arrow keys).
  useLayoutEffect(() => {
    const mode = scrollMode.current;
    if (!open || !container || !mode || !listRef.current) return;
    scrollMode.current = null;
    const node = listRef.current.querySelector(`[data-index="${active}"]`);
    if (!node) return;
    if (mode === "center") {
      listRef.current.scrollTop = node.offsetTop - (listRef.current.clientHeight - node.offsetHeight) / 2;
    } else if (node.scrollIntoView) {
      node.scrollIntoView({ block: "nearest" });
    }
  }, [open, container, active]);

  // Close on outside clicks, Escape (before dialogs see it), scrolling away and resizing.
  useEffect(() => {
    if (!open) return undefined;
    const doc = selectRef.current.ownerDocument;
    const onDocMouseDown = (e) => {
      const panel = panelRef.current;
      if ((panel && panel.contains(e.target)) || (selectRef.current && selectRef.current.contains(e.target))) return;
      // Hand focus back to the select so the click then moves focus on (and the caller's onBlur fires).
      closePopup(true);
    };
    const onWindowKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      closePopup(true);
    };
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      place();
    };
    doc.addEventListener("mousedown", onDocMouseDown, true);
    window.addEventListener("keydown", onWindowKeyDown, true);
    doc.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      doc.removeEventListener("mousedown", onDocMouseDown, true);
      window.removeEventListener("keydown", onWindowKeyDown, true);
      doc.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place, closePopup]);

  // Options can change while the popup is open (data still loading, value reset by the page).
  useEffect(() => {
    const el = selectRef.current;
    if (!open || !el || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => {
      const opts = readOptions(el);
      setOptions(opts);
      setSelectedIndex(el.selectedIndex);
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true, attributes: true });
    return () => observer.disconnect();
  }, [open]);

  const move = (step) => {
    const enabled = visible.filter((o) => !o.disabled);
    if (!enabled.length) return;
    const pos = enabled.findIndex((o) => o.index === active);
    let next;
    if (pos === -1) next = step > 0 ? 0 : enabled.length - 1;
    else next = Math.min(enabled.length - 1, Math.max(0, pos + step));
    scrollMode.current = "nearest";
    setActive(enabled[next].index);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      move(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "PageDown" || e.key === "PageUp") {
      e.preventDefault();
      move(e.key === "PageDown" ? 8 : -8);
    } else if (e.key === "Enter") {
      e.preventDefault(); // never submits the surrounding form
      choose(visible.find((o) => o.index === active));
    } else if (e.key === "Tab") {
      e.preventDefault();
      closePopup(true);
    }
  };

  const handleSearchChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    setActive(firstEnabled(filterOptions(options, next), next ? -1 : selectedIndex));
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  // Keep popup events away from the select's React ancestors (row clicks, drag handles, form keys).
  const stopReact = (e) => e.stopPropagation();
  const keepSearchFocus = (e) => {
    e.stopPropagation();
    if (e.target !== inputRef.current) e.preventDefault();
  };

  const activeId = active >= 0 ? `${listId}-opt-${active}` : undefined;

  const popup =
    open && container
      ? createPortal(
          <div
            ref={panelRef}
            className="ss-panel"
            style={font}
            onMouseDown={keepSearchFocus}
            onPointerDown={stopReact}
            onTouchStart={stopReact}
            onClick={stopReact}
            onDoubleClick={stopReact}
            onContextMenu={stopReact}
            onKeyDown={stopReact}
            onKeyUp={stopReact}
            onFocus={stopReact}
            onBlur={stopReact}
          >
            <div className="ss-search">
              <Search className="ss-search__icon" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                className="ss-search__input"
                value={query}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={activeId}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div ref={listRef} id={listId} role="listbox" className="ss-list">
              {visible.length === 0 ? (
                <div className="ss-empty">No matches for "{query.trim()}"</div>
              ) : (
                visible.map((o, i) => (
                  <React.Fragment key={o.index}>
                    {o.group && o.group !== (visible[i - 1] && visible[i - 1].group) && (
                      <div className="ss-group" role="presentation">
                        {o.group}
                      </div>
                    )}
                    <div
                      id={`${listId}-opt-${o.index}`}
                      data-index={o.index}
                      role="option"
                      aria-selected={o.index === selectedIndex}
                      aria-disabled={o.disabled || undefined}
                      className={[
                        "ss-option",
                        o.index === active && "is-active",
                        o.index === selectedIndex && "is-selected",
                        o.disabled && "is-disabled",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onMouseMove={() => {
                        if (!o.disabled && active !== o.index) setActive(o.index);
                      }}
                      onClick={() => choose(o)}
                    >
                      <span className="ss-option__label">{o.label || " "}</span>
                      {o.index === selectedIndex && <Check className="ss-option__check" aria-hidden="true" />}
                    </div>
                  </React.Fragment>
                ))
              )}
            </div>
          </div>,
          container
        )
      : null;

  return (
    <>
      <select
        {...selectProps}
        ref={setRefs}
        onPointerDown={handlePointerDown}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children}
      </select>
      {popup}
    </>
  );
});

export default SearchableSelect;
